import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth, getAuthHeaders } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, ArrowLeft, Users, MessageCircle, Search, Plus, X, Check, LogOut, Home } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MessagesPage() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [members, setMembers] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showNewChat, setShowNewChat] = useState(false);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [selectedGroupMembers, setSelectedGroupMembers] = useState([]);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!loading && user === false) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      fetchConversations();
      fetchMembers();
      sendHeartbeat();
      fetchOnline();

      const hbInterval = setInterval(sendHeartbeat, 10000);
      const onlineInterval = setInterval(fetchOnline, 10000);
      return () => {
        clearInterval(hbInterval);
        clearInterval(onlineInterval);
      };
    }
  }, [user]);

  useEffect(() => {
    if (selectedConv) {
      fetchMessages(selectedConv.id);
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(() => fetchMessages(selectedConv.id), 3000);
      return () => clearInterval(pollRef.current);
    }
  }, [selectedConv]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendHeartbeat = async () => {
    try { await axios.post(`${API}/portal/heartbeat`, {}, { headers: getAuthHeaders() }); } catch {}
  };

  const fetchOnline = async () => {
    try {
      const { data } = await axios.get(`${API}/portal/online`, { headers: getAuthHeaders() });
      setOnlineUsers(data);
    } catch {}
  };

  const fetchConversations = async () => {
    try {
      const { data } = await axios.get(`${API}/portal/conversations`, { headers: getAuthHeaders() });
      setConversations(data);
    } catch {}
  };

  const fetchMembers = async () => {
    try {
      const { data } = await axios.get(`${API}/portal/members`, { headers: getAuthHeaders() });
      setMembers(data);
    } catch {}
  };

  const fetchMessages = async (convId) => {
    try {
      const { data } = await axios.get(`${API}/portal/conversations/${convId}/messages`, { headers: getAuthHeaders() });
      setMessages(data);
    } catch {}
  };

  const startDM = async (memberId) => {
    try {
      const { data } = await axios.post(`${API}/portal/conversations`, {
        participant_ids: [memberId], is_group: false
      }, { headers: getAuthHeaders() });
      setShowNewChat(false);
      await fetchConversations();
      // Fetch full conversation with participant details
      const convRes = await axios.get(`${API}/portal/conversations`, { headers: getAuthHeaders() });
      const fullConv = convRes.data.find(c => c.id === data.id);
      setSelectedConv(fullConv || data);
      setMobileShowChat(true);
    } catch {}
  };

  const createGroup = async () => {
    if (!groupName.trim() || selectedGroupMembers.length === 0) return;
    try {
      const { data } = await axios.post(`${API}/portal/conversations`, {
        participant_ids: selectedGroupMembers, name: groupName, is_group: true
      }, { headers: getAuthHeaders() });
      setShowGroupForm(false);
      setGroupName("");
      setSelectedGroupMembers([]);
      await fetchConversations();
      const convRes = await axios.get(`${API}/portal/conversations`, { headers: getAuthHeaders() });
      const fullConv = convRes.data.find(c => c.id === data.id);
      setSelectedConv(fullConv || data);
      setMobileShowChat(true);
    } catch {}
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedConv) return;
    try {
      await axios.post(`${API}/portal/conversations/${selectedConv.id}/messages`,
        { content: newMessage }, { headers: getAuthHeaders() });
      setNewMessage("");
      fetchMessages(selectedConv.id);
      fetchConversations();
    } catch {}
  };

  const getInitials = (name) => name ? name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) : "?";

  const isOnline = (userId) => onlineUsers.includes(userId);

  const getConvName = (conv) => {
    if (conv.is_group) return conv.name || "Group Chat";
    const other = conv.participant_details?.find(p => p.id !== user?.id);
    return other?.name || "Unknown";
  };

  const getConvColor = (conv) => {
    if (conv.is_group) return "#FFB800";
    const other = conv.participant_details?.find(p => p.id !== user?.id);
    return other?.avatar_color || "#3b82f6";
  };

  const getConvOtherId = (conv) => {
    if (conv.is_group) return null;
    const other = conv.participant_details?.find(p => p.id !== user?.id);
    return other?.id;
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return "";
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "now";
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  };

  const filteredMembers = members.filter(m =>
    m.id !== user?.id && m.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) return <div className="min-h-screen flex items-center justify-center"><p className="font-['Satoshi'] text-zinc-500">Loading...</p></div>;
  if (!user) return null;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="messages-page">
      {/* Nav */}
      <nav className="bg-white border-b border-zinc-200 sticky top-0 z-50" data-testid="messages-nav">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <a href="/portal" className="font-['Clash_Display'] font-bold text-xl flex items-center gap-2">
            <ArrowLeft size={20} /> LIGHT CITY <span className="text-zinc-400">MESSAGES</span>
          </a>
          <div className="flex items-center gap-4">
            <a href="/portal" className="text-zinc-500 hover:text-black transition-colors"><Home size={20} /></a>
            <button onClick={() => { logout(); navigate("/"); }} className="text-zinc-500 hover:text-black transition-colors" data-testid="messages-logout">
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 py-6 h-[calc(100vh-64px)] flex gap-4">
        {/* Sidebar */}
        <aside className={`w-full md:w-80 bg-white border border-zinc-200 flex flex-col flex-shrink-0 ${mobileShowChat ? "hidden md:flex" : "flex"}`} data-testid="conversations-sidebar">
          <div className="p-4 border-b border-zinc-200">
            <div className="flex justify-between items-center mb-3">
              <h2 className="font-['Clash_Display'] font-bold text-xl">Messages</h2>
              <div className="flex gap-2">
                <button onClick={() => { setShowNewChat(!showNewChat); setShowGroupForm(false); }} className="w-8 h-8 flex items-center justify-center bg-zinc-100 hover:bg-zinc-200 transition-colors" data-testid="new-chat-btn">
                  <MessageCircle size={16} />
                </button>
                <button onClick={() => { setShowGroupForm(!showGroupForm); setShowNewChat(false); }} className="w-8 h-8 flex items-center justify-center bg-zinc-100 hover:bg-zinc-200 transition-colors" data-testid="new-group-btn">
                  <Users size={16} />
                </button>
              </div>
            </div>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search members..."
                className="input-sharp h-9 pl-9 text-sm bg-zinc-50"
                data-testid="search-members"
              />
            </div>
          </div>

          {/* New DM picker */}
          {showNewChat && (
            <div className="border-b border-zinc-200 max-h-48 overflow-y-auto" data-testid="new-chat-list">
              <p className="px-4 pt-3 font-['Satoshi'] text-xs font-bold text-zinc-500 uppercase tracking-wider">Start a conversation</p>
              {filteredMembers.map(m => (
                <button key={m.id} onClick={() => startDM(m.id)} className="w-full p-3 px-4 flex items-center gap-3 hover:bg-zinc-50 transition-colors" data-testid={`start-dm-${m.id}`}>
                  <div className="relative">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs" style={{ backgroundColor: m.avatar_color || "#3b82f6" }}>
                      {getInitials(m.name)}
                    </div>
                    {isOnline(m.id) && <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-white rounded-full" />}
                  </div>
                  <span className="font-['Satoshi'] text-sm font-medium">{m.name}</span>
                </button>
              ))}
            </div>
          )}

          {/* Group creation */}
          {showGroupForm && (
            <div className="border-b border-zinc-200 p-4 space-y-3" data-testid="group-form">
              <Input value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Group name..." className="input-sharp h-9 text-sm" data-testid="group-name-input" />
              <div className="max-h-32 overflow-y-auto space-y-1">
                {filteredMembers.map(m => (
                  <label key={m.id} className="flex items-center gap-2 p-1 cursor-pointer hover:bg-zinc-50">
                    <input type="checkbox" checked={selectedGroupMembers.includes(m.id)} onChange={() => {
                      setSelectedGroupMembers(prev => prev.includes(m.id) ? prev.filter(id => id !== m.id) : [...prev, m.id]);
                    }} className="rounded" />
                    <span className="font-['Satoshi'] text-sm">{m.name}</span>
                  </label>
                ))}
              </div>
              <Button onClick={createGroup} className="btn-sharp bg-black text-white text-sm w-full py-2" data-testid="create-group-btn">
                Create Group
              </Button>
            </div>
          )}

          {/* Conversation List */}
          <div className="flex-1 overflow-y-auto" data-testid="conversation-list">
            {conversations.map(conv => (
              <button
                key={conv.id}
                onClick={() => { setSelectedConv(conv); setMobileShowChat(true); }}
                className={`w-full p-4 flex items-center gap-3 hover:bg-zinc-50 transition-colors border-b border-zinc-50 text-left ${selectedConv?.id === conv.id ? "bg-zinc-100" : ""}`}
                data-testid={`conv-${conv.id}`}
              >
                <div className="relative flex-shrink-0">
                  {conv.is_group ? (
                    <div className="flex -space-x-2">
                      {conv.participant_details?.slice(0, 2).map((p, i) => (
                        <div key={i} className="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-white font-bold" style={{ backgroundColor: p.avatar_color || "#3b82f6", fontSize: "10px", zIndex: 2 - i }}>
                          {getInitials(p.name)}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-xs" style={{ backgroundColor: getConvColor(conv) }}>
                        {getInitials(getConvName(conv))}
                      </div>
                      {isOnline(getConvOtherId(conv)) && <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full" />}
                    </>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-baseline">
                    <p className="font-['Satoshi'] text-sm font-bold truncate">{getConvName(conv)}</p>
                    <span className="font-['Satoshi'] text-xs text-zinc-400 flex-shrink-0 ml-2">{timeAgo(conv.last_message_at)}</span>
                  </div>
                  <p className="font-['Satoshi'] text-xs text-zinc-500 truncate">
                    {conv.is_group ? <span className="text-[#FFB800] font-medium">Group</span> : (conv.last_message || "No messages yet")}
                    {!conv.is_group && conv.last_message ? "" : ""}
                  </p>
                </div>
              </button>
            ))}
            {conversations.length === 0 && (
              <div className="p-8 text-center">
                <MessageCircle size={32} className="text-zinc-300 mx-auto mb-3" />
                <p className="font-['Satoshi'] text-sm text-zinc-400">No conversations yet</p>
                <p className="font-['Satoshi'] text-xs text-zinc-400 mt-1">Start a new chat above</p>
              </div>
            )}
          </div>

          {/* Online Members Footer */}
          <div className="p-3 border-t border-zinc-200 bg-zinc-50">
            <p className="font-['Satoshi'] text-xs text-zinc-500 mb-2 flex items-center gap-1">
              <span className="w-2 h-2 bg-green-500 rounded-full inline-block" /> {onlineUsers.length} online
            </p>
          </div>
        </aside>

        {/* Chat Area */}
        <main className={`flex-1 bg-white border border-zinc-200 flex-col ${mobileShowChat ? "flex" : "hidden md:flex"}`} data-testid="chat-area">
          {selectedConv ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-zinc-200 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <button onClick={() => setMobileShowChat(false)} className="md:hidden text-zinc-500">
                    <ArrowLeft size={20} />
                  </button>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs" style={{ backgroundColor: getConvColor(selectedConv) }}>
                    {getInitials(getConvName(selectedConv))}
                  </div>
                  <div>
                    <p className="font-['Clash_Display'] font-semibold text-sm leading-none">{getConvName(selectedConv)}</p>
                    {!selectedConv.is_group && isOnline(getConvOtherId(selectedConv)) && (
                      <span className="font-['Satoshi'] text-[10px] text-green-500 font-bold">ACTIVE NOW</span>
                    )}
                    {selectedConv.is_group && (
                      <span className="font-['Satoshi'] text-[10px] text-zinc-400">{selectedConv.participant_details?.length} members</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 p-6 overflow-y-auto space-y-4 bg-zinc-50/30" data-testid="messages-list">
                {messages.map(msg => {
                  const isMine = msg.sender_id === user?.id;
                  return (
                    <div key={msg.id} className={`flex ${isMine ? "flex-col items-end" : "items-end gap-2 max-w-[80%]"}`} data-testid={`msg-${msg.id}`}>
                      {!isMine && (
                        <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold" style={{ backgroundColor: msg.sender_color || "#3b82f6", fontSize: "8px" }}>
                          {getInitials(msg.sender_name)}
                        </div>
                      )}
                      <div>
                        {!isMine && selectedConv.is_group && (
                          <p className="font-['Satoshi'] text-[10px] text-zinc-400 mb-1 ml-1">{msg.sender_name}</p>
                        )}
                        <div className={`p-3 text-sm max-w-md ${isMine
                          ? "bg-black text-white rounded-t-2xl rounded-bl-2xl"
                          : "bg-white border border-zinc-200 rounded-t-2xl rounded-br-2xl shadow-sm"
                        }`}>
                          <p className="font-['Satoshi']">{msg.content}</p>
                        </div>
                        <p className={`font-['Satoshi'] text-[10px] text-zinc-400 mt-1 ${isMine ? "text-right" : "ml-1"}`}>
                          {timeAgo(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })}
                {messages.length === 0 && (
                  <div className="text-center py-12">
                    <p className="font-['Satoshi'] text-zinc-400">No messages yet. Say hello!</p>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="p-4 border-t border-zinc-200 bg-white" data-testid="message-input-area">
                <div className="flex items-center gap-3 bg-zinc-100 px-4 py-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSend()}
                    placeholder="Send a message..."
                    className="bg-transparent border-none shadow-none focus-visible:ring-0 flex-1 text-sm h-10"
                    data-testid="message-input"
                  />
                  <button onClick={handleSend} className="text-black font-['Satoshi'] font-bold text-sm hover:text-zinc-600 transition-colors flex items-center gap-1" data-testid="send-message-btn">
                    <Send size={18} />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageCircle size={48} className="text-zinc-200 mx-auto mb-4" />
                <p className="font-['Clash_Display'] font-semibold text-xl text-zinc-400">Select a conversation</p>
                <p className="font-['Satoshi'] text-sm text-zinc-400 mt-1">or start a new one</p>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
